import type { ReactNode } from "react";

/**
 * Provenance + confidence badges (Req 27, 30.4). Every material derived value in
 * CoreFinOps carries its basis — the measure it came from and how trustworthy it
 * is — so the UI never shows a bare number without its context.
 */

const CONFIDENCE_COLORS: Record<string, string> = {
  Verified: "#086b2c",
  Actual: "#086b2c",
  Imported: "#195f9c",
  Estimated: "#8a6200",
  Inferred: "#9a4d00",
  High: "#086b2c",
  Medium: "#8a6200",
  Low: "#9a4d00",
  Unverified: "#666666",
};

/** Hex color → an rgba() tint for soft pill backgrounds. */
function tint(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export function ConfidenceBadge({ label }: { label?: string | null | undefined }): ReactNode {
  if (!label) return null;
  const color = CONFIDENCE_COLORS[label] ?? "#666666";
  return (
    <span
      aria-label={`confidence ${label}`}
      style={{
        display: "inline-block",
        fontSize: "0.7rem",
        fontWeight: 600,
        color,
        background: tint(color, 0.12),
        border: `1px solid ${tint(color, 0.3)}`,
        borderRadius: 999,
        padding: "1px 9px",
        lineHeight: "1.5",
      }}
    >
      {label}
    </span>
  );
}

/** Shows the measure a monetary value was computed from (e.g. Net Amortized). */
export function MeasureBadge({ measure }: { measure?: string | null | undefined }): ReactNode {
  if (!measure) return null;
  const pretty = measure
    .replace(/_/g, " ")
    .replace(/\bcost\b/i, "")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      title={`measure: ${measure}`}
      style={{ fontSize: "0.7rem", fontWeight: 600, color: "#55657a", background: "#eef2f7", borderRadius: 999, padding: "1px 9px", lineHeight: "1.5" }}
    >
      {pretty}
    </span>
  );
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#b00020",
  high: "#d13438",
  medium: "#8a6200",
  low: "#195f9c",
  info: "#666666",
};

export function SeverityBadge({ severity }: { severity?: string | null | undefined }): ReactNode {
  if (!severity) return null;
  const color = SEVERITY_COLORS[severity] ?? "#666666";
  return (
    <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#fff", background: color, borderRadius: 999, padding: "1px 9px", textTransform: "capitalize" }}>
      {severity}
    </span>
  );
}
